import { Edit, Person } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Fab,
  Stack,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resizeJPEGFiles } from '../helpers/image';

export default function ProfileImage({
  url,
  uploadUrl = '/uploads/users/photos',
  amplifyApi,
  onChange,
  isSignIn = false,
}) {
  const { t } = useTranslation('global');
  const theme = useTheme();
  const inputFile = useRef(null);
  const [imgSrc, setImgSrc] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setImgSrc(url);
  }, [url]);

  const onChangeInput = async event => {
    if (!event.target.files.length) return;
    setIsLoading(true);
    setImgSrc('');

    const [image] = await resizeJPEGFiles([...event.target.files]);

    const formDataFiles = new FormData();
    formDataFiles.append('files', image);

    amplifyApi(formDataFiles, uploadUrl, 'post').then(response => {
      const savedImage = response[0] ?? response;

      onChange(savedImage);

      const reader = new FileReader();
      reader.addEventListener('loadend', () => {
        setImgSrc(reader.result);
        setIsLoading(false);
      });
      reader.readAsDataURL(image);
    });
  };

  const deletePhoto = () => {
    onChange(null);
    setImgSrc('');
  };

  const handleButtonPosition = (signIn, imgS) => {
    if (signIn) return '-10px';
    if (!signIn && !!imgS) return '46px';
    return '-16px';
  };

  return (
    <Stack gap={4} sx={{ position: 'relative' }}>
      <Box
        width={isSignIn ? '90px' : '186px'}
        height={isSignIn ? '90px' : '186px'}
        sx={{
          background: theme.palette.grey[100],
          borderRadius: isSignIn ? '50%' : '4px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <input
          type="file"
          multiple={false}
          onChange={onChangeInput}
          ref={inputFile}
          data-testid="imageFile"
          accept="image/jpeg"
          hidden
        />
        {isLoading && <CircularProgress />}
        {!imgSrc && !isLoading && (
          <Person
            sx={{
              fontSize: isSignIn ? '50px' : '74px',
              color: theme.palette.action.disabled,
            }}
          />
        )}
        {!!imgSrc && (
          <img
            src={imgSrc}
            alt="ProfileImage"
            data-testid="profileImage"
            style={{
              maxHeight: '186px',
              maxWidth: '186px',
              borderRadius: '4px',
            }}
          />
        )}
      </Box>
      <Fab
        aria-label="edit"
        size="small"
        onClick={() => inputFile.current.click()}
        sx={{
          position: 'absolute',
          bottom: handleButtonPosition(isSignIn, imgSrc),
          right: isSignIn ? '-15px' : '16px',
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Edit />
      </Fab>
      {!!imgSrc && !isSignIn && (
        <Button size="small" onClick={deletePhoto}>
          {t('modalProfile.deletePhoto')}
        </Button>
      )}
    </Stack>
  );
}
